import {
  File,
  FilesAndFolders,
  Resource,
  ResourceAcknowledgement,
} from '@nextforce/api'
import { t } from 'app/i18l'
import { resolveMime } from 'friendly-mimes'
import { FileFolderSortOption } from './types'
import { FILTER_ITEMS } from './views/filters_row'

export function getFileFolderList(data: FilesAndFolders | undefined) {
  const list: Resource[] = []
  if (data?.folders) {
    list.push(...data.folders)
  }
  if (data?.files) {
    list.push(...data.files)
  }
  return list
}

export function sortResourceList(
  list: Resource[] | undefined,
  option: FileFolderSortOption
): Resource[] {
  if (!list) {
    return []
  }
  return list.sort((a, b) => {
    if (option === 'nameAsc') {
      return a.name.localeCompare(b.name)
    }
    if (option === 'dateAsc') {
      if (a.updated && b.updated) {
        return a.updated - b.updated
      }
      if (a.created && b.created) {
        return a.created - b.created
      }
    }
    if (option === 'dateDesc') {
      if (a.updated && b.updated) {
        return b.updated - a.updated
      }
      if (a.created && b.created) {
        return b.created - a.created
      }
    }
    if (option === 'sizeDesc') {
      if (
        a.type === 'file' &&
        b.type === 'file' &&
        a.currentVersion?.size &&
        b.currentVersion?.size
      ) {
        return b.currentVersion.size - a.currentVersion.size
      }
    }
    return 0
  })
}

export function filterResourceList(
  list: Resource[] | undefined,
  filterId: number
): Resource[] {
  if (!list) return []

  let filteredList = list

  switch (filterId) {
    case FILTER_ITEMS.all.id:
      break
    case FILTER_ITEMS.documents.id:
      filteredList = list.filter(
        resource =>
          resource.type === 'file' &&
          !isImageType(resource.currentVersion?.fileMimeType) &&
          !isVideoType(resource.currentVersion?.fileMimeType) &&
          !resource.currentVersion?.externalUrl
      )
      break
    case FILTER_ITEMS.folders.id:
      filteredList = list.filter(resource => resource.type === 'folder')
      break
    case FILTER_ITEMS.photosVideos.id:
      filteredList = list.filter(
        resource =>
          resource.type === 'file' &&
          (isImageType(resource.currentVersion?.fileMimeType) ||
            isVideoType(resource.currentVersion?.fileMimeType))
      )
      break
    case FILTER_ITEMS.webLinks.id:
      filteredList = list.filter(
        resource =>
          resource.type === 'file' &&
          !isImageType(resource.currentVersion?.fileMimeType) &&
          !isVideoType(resource.currentVersion?.fileMimeType) &&
          !isPdfType(resource.currentVersion?.fileMimeType)
      )
      break
  }

  return filteredList
}

export const getFileSize = (size: number | undefined): string => {
  if (!size) {
    return ''
  }
  if (size < 1024) {
    return `${size} bytes`
  }
  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(2)} KB`
  }
  return `${(size / (1024 * 1024)).toFixed(2)} MB`
}

export const getTimeFromNow = (time: number): string => {
  const now = Date.now()
  const diff = now - time

  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)

  if (minutes < 1) {
    return 'Just now'
  } else if (minutes < 60) {
    return `${minutes} mins ago`
  } else if (hours < 24) {
    return `${hours} hours ago`
  } else {
    return new Date(time).toLocaleDateString()
  }
}

export const getPathName = (pathList: Resource['path']): string | undefined => {
  let pathName = `${t('files')}/`
  if (pathList) {
    pathName = pathName.concat(pathList?.map(path => path.name).join('/'))
  }
  return pathName
}

export function isImageType(mimeType?: string): boolean {
  return mimeType?.startsWith('image') ?? false
}

export const isGifType = (mimeType?: string): boolean => {
  return mimeType === 'image/gif'
}

export function isVideoType(mimeType?: string): boolean {
  return mimeType?.startsWith('video') ?? false
}

export function isPdfType(mimeType?: string): boolean {
  return mimeType === 'application/pdf'
}

export function getFriendlyMimeType(mimeType?: string): string {
  if (!mimeType) {
    return 'Unknown'
  }
  const result = resolveMime(mimeType)
  return result.fileType.substring(1, result.fileType.length).toUpperCase()
}

export type AcknowledgementSection =
  | {
      type: 'header'
      sectionTitle?: string
    }
  | {
      type: 'acknowledgement'
      acknowledgement?: ResourceAcknowledgement
    }
  | {
      type: 'file'
      file: File
    }

export const ACKNOWLEDGEMENT_SECTION_PRIORITY = {
  [t('overdue')]: 0,
  [t('dueToday')]: 1,
  [t('dueThisWeek')]: 2,
  [t('dueLater')]: 3,
  [t('completed')]: 4,
}

export function getDaysUntilDue(dueDate: Date | string | number): number {
  const due = new Date(dueDate)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return Math.floor((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
}

export function getAcknowledgementSectionTitle(
  daysUntilDue: number,
  isAcknowledged: boolean
): string {
  if (isAcknowledged) return t('completed')
  if (daysUntilDue < 0) return t('overdue')
  if (daysUntilDue === 0) return t('dueToday')
  if (daysUntilDue <= 7) return t('dueThisWeek')
  return t('dueLater')
}

export function sortByAcknowledgementSection<
  T extends { sectionTitle: string }
>(a: T, b: T): number {
  return (
    ACKNOWLEDGEMENT_SECTION_PRIORITY[a.sectionTitle] -
    ACKNOWLEDGEMENT_SECTION_PRIORITY[b.sectionTitle]
  )
}

export function createSectionedList<T extends { sectionTitle: string }>(
  items: T[],
  createItem: (item: T) => AcknowledgementSection
): AcknowledgementSection[] {
  const result: AcknowledgementSection[] = []
  let currentSection = ''

  items.forEach(item => {
    if (currentSection !== item.sectionTitle) {
      result.push({ type: 'header', sectionTitle: item.sectionTitle })
      currentSection = item.sectionTitle
    }
    result.push(createItem(item))
  })

  return result
}

export function getAcknowledgementSectionKey(
  item: AcknowledgementSection,
  index: number
): string {
  if (item.type === 'header') {
    return `header-${item.sectionTitle}`
  }
  if (item.type === 'file' && item.file?.id) {
    return `file-${item.file.id}`
  }
  if (item.type === 'acknowledgement' && item.acknowledgement?.id) {
    return `acknowledgement-${item.acknowledgement.id}`
  }
  return `item-${index}`
}
